import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { Search, Clock, ChevronRight, ChevronDown, ShieldAlert, Lock, Calculator, BookOpen, CheckCircle2 } from "lucide-react";
import { CATEGORIES, CALCULATORS, getCalculator } from "@/lib/catalog";
import { getRecent } from "@/lib/storage";
import { getFieldDataIndex } from "@/lib/fieldData";
import { useEntitlement } from "@/lib/entitlement";
import { isProActive } from "@/lib/featureAccess";
import { useStackMode } from "@/lib/stackModeContext";
import { listRigSheets, getRigSheet } from "@/lib/rigsheet/storage";
import { listWellProfiles, getWellProfile } from "@/lib/well/storage";
import { validateRigSheet } from "@/lib/rigsheet/schema";
import { validateWellProfile } from "@/lib/well/schema";
import { isWellProfileComplete } from "@/lib/well/completion";
import { getEngineeringSession } from "@/lib/rce/session";
import { LABELS, SETUP_STATUS } from "@/lib/displayLabels";
import { CurrentOperationsCard } from "@/components/CurrentOperationsCard";
import RecentOpStatesLauncher from "@/components/RecentOpStatesLauncher";

function ProLockBadge() {
  return (
    <span
      data-testid="pro-lock-badge"
      className="label-up text-[9px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 shrink-0"
      style={{ background: "rgba(255,179,0,0.18)", color: "var(--rs-amber)", border: "1px solid rgba(255,179,0,0.45)" }}
      title="Requires RigSentinel Pro"
    >
      <Lock className="w-2.5 h-2.5" /> Pro
    </span>
  );
}

function StatusBadge({ status }) {
  const s = SETUP_STATUS[status] || SETUP_STATUS.not_started;
  const done = status === "ready" || status === "connected";
  return (
    <span className="label-up text-[8.5px] px-1.5 py-0.5 rounded shrink-0 inline-flex items-center gap-1"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {done && <CheckCircle2 className="w-2.5 h-2.5" />}
      {s.label}
    </span>
  );
}

function StepChip({ n }) {
  return (
    <span
      data-testid={`home-setup-step-${n}`}
      className="inline-flex items-center justify-center shrink-0 w-6 h-6 rounded-full font-mono-rs text-[11px] font-bold"
      style={{ background: "rgba(245,180,0,0.16)", color: "var(--rs-amber)", border: "1px solid rgba(245,180,0,0.6)" }}
      aria-label={`Step ${n}`}
    >
      {n}
    </span>
  );
}

// Primary Engineering Setup step card (recommended order — never mandatory).
function SetupCard({ n, icon: Icon, title, sub, status, onClick, testid, last }) {
  const done = status === "ready" || status === "connected";
  const inProgress = status === "in_progress";
  return (
    <div className="relative">
      <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        className="card-rs px-3.5 py-3 flex items-center gap-3 text-left transition-colors hover:border-[var(--rs-amber)] w-full"
        style={{
          background: "rgba(22,24,29,0.65)",
          borderColor: done ? "rgba(74,222,128,0.5)" : inProgress ? "rgba(255,179,0,0.4)" : "var(--rs-border)",
        }}
      >
        <StepChip n={n} />
        <Icon className="w-5 h-5 shrink-0" style={{ color: "var(--rs-amber)" }} strokeWidth={2} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13.5px] font-semibold text-[var(--rs-text-1)]">{title}</span>
            <StatusBadge status={status} />
          </div>
          <div className="text-[11px] text-[var(--rs-text-3)] mt-0.5">{sub}</div>
        </div>
        <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
      </button>
      {!last && (
        <div className="absolute left-[26px] -bottom-2.5 h-2.5 w-px" style={{ background: "rgba(245,180,0,0.35)" }} aria-hidden="true" />
      )}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [recentOpen, setRecentOpen] = useState(false);
  const recent = useMemo(() => getRecent().map(getCalculator).filter(Boolean).slice(0, 10), []);
  const ent = useEntitlement();
  const isPro = isProActive(ent);
  // Existing global Surface/Subsea toggle (header) — single source of
  // truth for the Kill Sheets card below. No new selector introduced.
  const { stackMode } = useStackMode();
  // P0 repoint (Phase 1) — Home Kill Sheets card now routes into the
  // canonical System B well-scoped Kill Sheet library instead of the
  // legacy System A /kill-sheet/:variant routes. Both buttons resolve the
  // active well from the existing Engineering Session.
  const goToKillSheets = () => {
    const session = getEngineeringSession();
    const wellId = session && session.wellId;
    navigate(wellId ? `/wells/${wellId}/kill-sheets/new` : "/wells?intent=kill-sheets");
  };

  // Truthful setup status from EXISTING data only — never "ready because the
  // page was opened", no invented percentages. Conservative when unknown.
  //
  // Criteria-based, NOT existence-based: a record only reads COMPLETE when it
  // actually passes its own validator. A record that exists but still fails
  // validation (e.g. a Well Profile where only the Identity section has been
  // saved, which needs nothing but a well name) is IN PROGRESS — the state
  // that previously could never be reached.
  const setup = useMemo(() => {
    const safeList = (fn) => { try { const v = fn(); return Array.isArray(v) ? v : []; } catch { return []; } };
    const passes = (validator, rec) => { try { return Boolean(validator(rec)?.ok); } catch { return false; } };

    const rigRows = safeList(listRigSheets);
    const rig = rigRows.length === 0
      ? "not_started"
      : rigRows.some((r) => passes(validateRigSheet, r.payload)) ? "ready" : "in_progress";

    // A well only reads COMPLETE when every REQUIRED section actually
    // carries data — the same bar the editor's own per-section badges use
    // (lib/well/completion.js). validateWellProfile alone is not a
    // completeness test: Classification and Status ship with non-empty
    // schema defaults, so a well where only Identity was ever saved passes
    // whole-record validation while the rest sits untouched. Both checks
    // must hold — schema-valid AND every required section filled in.
    const wellRows = safeList(listWellProfiles);
    const wellComplete = (r) => {
      try { return isWellProfileComplete(r) && passes(validateWellProfile, r); }
      catch { return false; }
    };
    const well = wellRows.length === 0
      ? "not_started"
      : wellRows.some(wellComplete) ? "ready" : "in_progress";

    // Rig Engineering is a *connected* context: it needs a rig AND a well that
    // both still exist. One side on its own is exactly what the Rig Sheet and
    // Well Profile editors auto-link on first save, so it is real but partial
    // context — IN PROGRESS, never COMPLETE.
    let eng = "not_started";
    try {
      const s = getEngineeringSession();
      const hasRig  = Boolean(s?.rigId  && getRigSheet(s.rigId));
      const hasWell = Boolean(s?.wellId && getWellProfile(s.wellId));
      if (hasRig && hasWell) eng = "ready";
      else if (hasRig || hasWell || s?.projectId || s?.currentOperation) eng = "in_progress";
    } catch { /* conservative */ }

    return { rig, well, eng };
  }, []);

  useEffect(() => {
    if (loc.hash === "#engineering-setup") {
      setTimeout(() => document.getElementById("engineering-setup")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [loc.hash]);

  const results = useMemo(() => {
    if (!q.trim()) return null;
    const norm = (s) => String(s || "").toLowerCase().replace(/³/g, "3").replace(/²/g, "2").replace(/·/g, ".");
    const t = norm(q.trim());
    const calcs = CALCULATORS.filter((c) => norm(c.name).includes(t) || norm(c.description).includes(t));
    const cats = CATEGORIES.filter((c) => norm(c.name).includes(t) || norm(c.short).includes(t));
    const fieldData = getFieldDataIndex().filter((f) => norm(f.title).includes(t) || norm(f.body).includes(t)).slice(0, 12);
    return { calcs, cats, fieldData };
  }, [q]);

  return (
    <div data-testid="dashboard-root" className="dashboard-root flex flex-col gap-5 bg-grain pb-2">
      {/* Compact identity — no oversized hero */}
      <section className="pt-1">
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">Dashboard</div>
        <h2 className="font-heading font-black text-2xl tracking-tight mt-0.5">RigSentinel</h2>
      </section>

      {/* How to Use RigSentinel */}
      <button
        type="button"
        data-testid="home-howto-card"
        onClick={() => navigate("/how-to-use")}
        className="card-rs px-4 py-2.5 sm:py-3 flex items-center gap-3 text-left transition-colors hover:border-[var(--rs-amber)]"
        style={{ background: "rgba(22,24,29,0.65)", borderLeft: "2px solid var(--rs-amber)" }}
      >
        <BookOpen className="w-5 h-5 shrink-0" style={{ color: "var(--rs-amber)" }} strokeWidth={2} />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold">How to Use RigSentinel</div>
          <p className="text-[11px] text-[var(--rs-text-3)] mt-0.5 leading-tight sm:leading-snug">
            New to RigSentinel? Learn how to get started, set up your engineering workspace and find the tools you need.
          </p>
          <span className="font-mono-rs text-[10px] tracking-[0.18em] uppercase mt-0.5 sm:mt-1 inline-flex items-center gap-1 font-semibold" style={{ color: "var(--rs-amber)" }}>
            Start Here <ChevronRight className="w-3 h-3" />
          </span>
        </div>
      </button>

      {/* Engineering Setup — dominant upper section. Recommended, never mandatory. */}
      <section id="engineering-setup" data-testid="home-engineering-setup-section" className="flex flex-col gap-2.5">
        <div>
          <div className="label-up text-[var(--rs-amber)]">Engineering Setup</div>
          <div className="text-[11px] text-[var(--rs-text-3)] mt-0.5">Set up your engineering workspace in three steps.</div>
        </div>
        <SetupCard n={1} icon={Icons.TowerControl} title="Rig Data Sheet" sub="Define the rig"
          status={setup.rig} onClick={() => navigate("/rig-sheets")} testid="home-rig-sheet-card" />
        <SetupCard n={2} icon={Icons.Layers} title="Well Profiles" sub="Define the well"
          status={setup.well} onClick={() => navigate("/wells")} testid="home-wells-card" />
        <SetupCard n={3} icon={Icons.DraftingCompass} title={LABELS.engSession} sub="Connect the engineering context"
          status={setup.eng === "ready" ? "ready" : "not_started"}
          onClick={() => window.dispatchEvent(new CustomEvent("rce:open-session-switcher"))}
          testid="home-rig-engineering-card" last />
      </section>

      <CurrentOperationsCard />

      {/* Operational State is retained for legacy/secondary workflows (the
          old Kill Sheet creation path), not part of the primary 3-step
          setup sequence above - kept visually secondary to Current
          Operations per customer request, not styled as a setup step. */}
      <div className="opacity-80" data-testid="home-opstate-legacy-section">
        <div className="text-[9.5px] label-up text-[var(--rs-text-3)] mb-1.5">
          Legacy · Operational State
        </div>
        <RecentOpStatesLauncher />
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rs-text-3)]" />
        <input
          data-testid="search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search formulas, converters, or field data"
          className="input-rs search-input-rs pl-9 pr-3"
        />
      </div>

      {results ? (
        <SearchResults results={results} onPick={(c) => navigate(`/calc/${c.id}`)} onPickCategory={(c) => navigate(`/categories/${c.id}`)} />
      ) : (
        <>
          {/* Engineering Workspace — secondary cards */}
          <section data-testid="home-engineering-section">
            <div className="label-up text-[var(--rs-text-3)] mb-3">Engineering Workspace</div>
            <button
              type="button"
              data-testid="home-engineering-calculators-card"
              onClick={() => navigate("/categories")}
              className="card-rs px-4 py-3.5 flex items-center gap-3 text-left transition-colors hover:border-[var(--rs-amber)] w-full"
            >
              <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                style={{ background: "rgba(255,179,0,0.12)", border: "1px solid rgba(255,179,0,0.3)" }}>
                <Calculator className="w-4.5 h-4.5 w-5 h-5" style={{ color: "var(--rs-amber)" }} strokeWidth={2.25} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold">Engineering Calculators</div>
                <div className="text-[11px] text-[var(--rs-text-3)] mt-0.5">Oilfield engineering calculations and unit conversions.</div>
              </div>
              <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
            </button>

            {/* Kill Sheets — navigation only. Driven entirely by the
                existing global Surface/Subsea toggle (useStackMode); no
                second selector, no Kill Sheet engine/schema/storage/route
                change. Reuses the 4 existing standalone routes. */}
            <div data-testid="home-kill-sheets-card" className="mt-2 w-full card-rs p-3.5">
              <div className="flex items-center gap-3 mb-2.5">
                <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                  style={{
                    background: stackMode === "subsea" ? "rgba(33,150,243,0.12)" : "rgba(255,179,0,0.12)",
                    border: `1px solid ${stackMode === "subsea" ? "rgba(33,150,243,0.3)" : "rgba(255,179,0,0.3)"}`,
                  }}>
                  {stackMode === "subsea"
                    ? <Icons.Waves className="w-4.5 h-4.5" style={{ color: "#60A5FA" }} strokeWidth={2.25} />
                    : <ShieldAlert className="w-4.5 h-4.5" style={{ color: "var(--rs-amber)" }} strokeWidth={2.25} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-semibold">Kill Sheets</div>
                  <div className="label-up text-[10px] mt-0.5" data-testid="home-kill-sheets-mode-label"
                    style={{ color: stackMode === "subsea" ? "#60A5FA" : "var(--rs-amber)" }}>
                    {stackMode === "subsea" ? "Subsea · Vertical & Horizontal" : "Surface · Vertical & Horizontal"}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="home-kill-sheet-vertical"
                  onClick={goToKillSheets}
                  className="px-3 py-2.5 rounded-md border border-[var(--rs-border)] text-[12px] font-medium text-left hover:border-[var(--rs-amber)] transition-colors"
                >
                  Vertical Kill Sheet
                </button>
                <button
                  type="button"
                  data-testid="home-kill-sheet-horizontal"
                  onClick={goToKillSheets}
                  className="px-3 py-2.5 rounded-md border border-[var(--rs-border)] text-[12px] font-medium text-left hover:border-[var(--rs-amber)] transition-colors"
                >
                  Horizontal Kill Sheet
                </button>
              </div>
            </div>

            <button data-testid="saved-sheets-btn" onClick={() => navigate("/saved")}
              className="mt-2 w-full card-rs p-3.5 flex items-center justify-between hover:border-[var(--rs-amber)] transition-colors">
              <div className="flex items-center gap-3">
                <Icons.Bookmark className="w-4 h-4 text-[var(--rs-amber)]" />
                <div className="text-sm font-semibold">Saved Sheets</div>
              </div>
              <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)]" />
            </button>
            <button data-testid="home-reflibs-card" onClick={() => navigate("/reference-libraries")}
              className="mt-2 w-full card-rs p-3.5 flex items-center justify-between hover:border-[#78c8ff] transition-colors text-left">
              <div className="flex items-center gap-3 min-w-0 pr-3">
                <Icons.Library className="w-4 h-4 shrink-0" style={{ color: "#78c8ff" }} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">Reference Libraries</div>
                  <div className="text-[11px] text-[var(--rs-text-3)] truncate">Drill pipe, HWDP, drill collars, casing and tubing catalogs.</div>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
            </button>
            <button data-testid="home-subsea-card" onClick={() => navigate("/categories/wellcontrol/subsea")}
              className="mt-2 w-full card-rs p-3.5 flex items-center justify-between text-left transition-colors"
              style={{ borderColor: "rgba(33,150,243,0.4)" }}>
              <div className="flex items-center gap-3 min-w-0 pr-3">
                <Icons.Waves className="w-4 h-4 shrink-0" style={{ color: "#60A5FA" }} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">Subsea Stack Well Control</div>
                  <div className="text-[11px] text-[var(--rs-text-3)] truncate">Riser, choke/kill geometry, subsea-specific calculations.</div>
                </div>
              </div>
              {!isPro && <ProLockBadge />}
              <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
            </button>
            <button data-testid="home-ddr-shortcut" onClick={() => navigate("/reports/daily-drilling-report")}
              className="mt-2 w-full card-rs p-3.5 flex items-center justify-between text-left transition-colors hover:border-[var(--rs-amber)]">
              <div className="flex items-center gap-3 min-w-0 pr-3">
                <Icons.FileText className="w-4 h-4 shrink-0" style={{ color: "var(--rs-amber)" }} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">Daily Drilling Report</div>
                  <div className="text-[11px] text-[var(--rs-text-3)] truncate">Rough notes → polished professional DDR.</div>
                </div>
              </div>
              {!isPro && <ProLockBadge />}
              <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
            </button>
          </section>

          {/* Recently Used — collapsed accordion, up to 10 genuine tools */}
          {recent.length > 0 && (
            <section data-testid="home-recent-section">
              <button
                type="button"
                data-testid="recent-toggle"
                onClick={() => setRecentOpen((v) => !v)}
                className="w-full card-rs px-3.5 py-3 flex items-center justify-between text-left"
                aria-expanded={recentOpen}
              >
                <span className="label-up text-[var(--rs-text-3)] inline-flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" /> Recently Used
                </span>
                <ChevronDown className="w-4 h-4 text-[var(--rs-text-3)] transition-transform" style={{ transform: recentOpen ? "rotate(180deg)" : "none" }} />
              </button>
              {recentOpen && (
                <div className="flex flex-col gap-2 mt-2" data-testid="recent-list">
                  {recent.map((c) => (
                    <button key={c.id} data-testid={`recent-${c.id}`} onClick={() => navigate(`/calc/${c.id}`)}
                      className="card-rs p-3 flex items-center justify-between text-left">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{c.name}</div>
                        <div className="text-[11px] text-[var(--rs-text-3)] uppercase tracking-[0.1em] mt-0.5">
                          {CATEGORIES.find((x) => x.id === c.category)?.name}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Engineering Safety — compact, near bottom, links to full disclaimer */}
          <button
            type="button"
            data-testid="home-safety-disclaimer"
            onClick={() => navigate("/safety")}
            className="card-rs px-3 py-2 sm:py-2.5 flex items-center gap-2.5 text-left transition-colors hover:border-[var(--rs-amber)]"
          >
            <ShieldAlert className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--rs-amber)" }} />
            <div className="min-w-0 flex-1">
              <span className="text-[11px] font-semibold text-[var(--rs-text-2)]">Engineering Safety</span>
              <p className="text-[10.5px] text-[var(--rs-text-3)] leading-tight sm:leading-snug">
                Verify critical inputs and results before operational use. View the full Engineering &amp; Safety Disclaimer.
              </p>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-[var(--rs-text-3)] shrink-0" />
          </button>

          {!isPro && (
            <section className="card-rs p-4">
              <div className="label-up text-[var(--rs-amber)] mb-2">Upgrade</div>
              <div className="text-sm font-semibold mb-1">Unlock the full calculator suite</div>
              <div className="text-xs text-[var(--rs-text-3)] mb-3">All calculators, saved sheets, favorites, exports.</div>
              <button data-testid="view-plans-btn" onClick={() => navigate("/pricing")} className="btn-amber h-10 px-4 rounded-md text-xs label-up">View Pricing</button>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function SearchResults({ results, onPick, onPickCategory }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-4" data-testid="search-results">
      {results.cats.length > 0 && (
        <section>
          <div className="label-up text-[var(--rs-text-3)] mb-2">Categories</div>
          <div className="flex flex-col gap-2">
            {results.cats.map((c) => (
              <button key={c.id} data-testid={`search-cat-${c.id}`} onClick={() => onPickCategory(c)} className="card-rs p-3 text-left">
                <div className="font-semibold text-sm">{c.name}</div>
                <div className="text-[11px] text-[var(--rs-text-3)] mt-0.5">{c.desc}</div>
              </button>
            ))}
          </div>
        </section>
      )}
      <section>
        <div className="label-up text-[var(--rs-text-3)] mb-2">Calculators ({results.calcs.length})</div>
        <div className="flex flex-col gap-2">
          {results.calcs.map((c) => (
            <button key={c.id} data-testid={`search-calc-${c.id}`} onClick={() => onPick(c)} className="card-rs p-3 text-left">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-sm">{c.name}</div>
                {c.comingSoon && <span className="label-up text-[9px] px-1.5 py-0.5 rounded shrink-0" style={{ background: "rgba(255,179,0,0.15)", color: "var(--rs-amber)" }}>Soon</span>}
              </div>
              <div className="text-[11px] text-[var(--rs-text-3)] mt-0.5 line-clamp-1">{c.description}</div>
            </button>
          ))}
        </div>
      </section>
      {results.fieldData && results.fieldData.length > 0 && (
        <section>
          <div className="label-up text-[var(--rs-text-3)] mb-2">Field Data ({results.fieldData.length})</div>
          <div className="flex flex-col gap-2">
            {results.fieldData.map((f, i) => (
              <button key={`${f.title}-${i}`} data-testid={`search-fd-${i}`} onClick={() => navigate(f.target || "/library")} className="card-rs p-3 text-left">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-semibold text-sm">{f.title}</div>
                  <div className="label-up text-[9px] text-[var(--rs-text-3)]">{f.type}</div>
                </div>
                <div className="text-[11px] text-[var(--rs-text-2)] mt-0.5 line-clamp-2">{f.body}</div>
              </button>
            ))}
          </div>
        </section>
      )}
      {results.calcs.length === 0 && results.cats.length === 0 && (!results.fieldData || results.fieldData.length === 0) && (
        <div className="text-sm text-[var(--rs-text-3)] py-6 text-center">No results.</div>
      )}
    </div>
  );
}
